/* eslint-disable array-bracket-newline */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Log } from 'cv-dialog-sdk';
import { onTransitionRegistry, constants } from 'cv-react-core';

import { MyVrMonikerLookup, GDS2DIdMap, GDS3DIdMap, MCDColor, Vec3 } from '@ppm/mcd-utils-js';
import { GVC, GVCModelObject, GVC_ENUM, GVCSAMAuth, GVCOKTAAuth, GVCPropertyObject, GVCTransform } from '@ppm/gvc-react';

/**
 * PPM View Control Wrapper
 * This component is used to display graphical data generated by Xalt Visualization and PPM View Control.
 * PPM View Control is located at: https://dev.azure.com/hexagonPPMInnerSource/Visualization/_packaging?_a=feed&feed=PPM%40Release
 * Models used to load graphical data
 * @property { object } style
 * @property { object } xStyle
 * @property { bool } isLibraryLoaded
 * @property { object } view
 * @property { number } height
 * @property { number } width
 * @property { object } properties
 * @property { func } onSelectionChanged
 * @property { func } onModelLoaded
 * @property { func } onViewerLoaded
 * @property { func } onWebGVCLibLoaded
 * @property { func } onWindowResize
 */
class PPMViewControl extends PureComponent {
    static propTypes = {
        // style: PropTypes.oneOfType([
        //     PropTypes.object,
        //     PropTypes.array,
        // ]),
        // xStyle: PropTypes.oneOfType([
        //     PropTypes.object,
        //     PropTypes.array,
        // ]),
        isViewerLoaded: PropTypes.bool,
        height: PropTypes.number,
        width: PropTypes.number,
        models: PropTypes.object,
        componentProperties: PropTypes.object,
        modelProperties: PropTypes.object,
        graphicProperties: PropTypes.object,
        onSelectionChanged: PropTypes.func,
        onViewerLoaded: PropTypes.func,
        onSetLegacyComponentProperties: PropTypes.func, // TODO GVC DEPRECATION: REMOVE
        isLegacyImpl: PropTypes.bool, // TODO GVC DEPRECATION: REMOVE
        onLoadGraphicProperties: PropTypes.func,
    };

    static defaultProps = {
        models: {},
        isViewerLoaded: false,
        height: 600,
        width: 800,
        onSelectionChanged: () => {},
        onViewerLoaded: () => {},
        onLoadGraphicProperties: () => {},
    };

    constructor(props) {
        super(props);
        this.handleOnViewerLoaded = this.handleOnViewerLoaded.bind(this);
        this.loadFileMonikerConverter = this.loadFileMonikerConverter.bind(this);
        this.handleSelectionChanged = this.handleSelectionChanged.bind(this);
    }

    render() {
        const {
            isViewerLoaded,
            height,
            width,
            models,
            componentProperties,
            graphicProperties,
            modelProperties,
            onSetLegacyComponentProperties,
            isLegacyImpl } = this.props;

        let propertyData;
        if (graphicProperties) {
            const {
                title,
                properties,
                documents } = graphicProperties;
            propertyData = new GVCPropertyObject(title, properties, documents);
        }

        const viewProps = {
            resources: '/gvc-resources/',
            height,
            width,
            models: isViewerLoaded ? this.createGVCModelObject({ ...modelProperties, ...models }, isLegacyImpl, onSetLegacyComponentProperties, modelProperties) : [],
            ...componentProperties,
            errors: this.componentDidCatch,
            properties: propertyData,
            selectionChanged: this.handleSelectionChanged,
            viewerLoaded: this.handleOnViewerLoaded,
            workerJSPath: '/worker.js',
        };

        return React.createElement(GVC, { ...viewProps });
    }

    componentDidUpdate(prevProps) {
        const { models: prevModels } = prevProps;
        const { models } = this.props;
        const prevModelsJSON = JSON.stringify(prevModels);
        const currentModelsJSON = JSON.stringify(models);
        this.isModelDataSame = false;
        // Hack because GVC blows up if you recreate the model object. You have to recreate the model object
        // because you have to send it on updates.
        if (currentModelsJSON.localeCompare(prevModelsJSON) === 0) {
            this.isModelDataSame = true;
        }
    }

    componentWillUnmount() {
        // Unload the myVr lookup hook. This will be replaced in furture releases of the GVC.
        if (this.monikerLookup && this.monikerLookup.isLoaded) {
            this.monikerLookup.closeBinFile();
            this.monikerLookup = null;
        }

        // Unregister any registry entries if some are left behind
        onTransitionRegistry.unsubscribe();
    }

    componentDidCatch(error, info) {
        // You can also log the error to an error reporting service
        const message = `${error}:\r\n\t${info}`;
        Log.error(message);
    }


    getEnumValue = (value, enumObject) => {
        const objectKey = Object.keys(enumObject).find((key) => enumObject[key].toLowerCase() === value.toLowerCase());
        return objectKey ? enumObject[objectKey] : value;
    }

    createGVCModelObject(modelData, isLegacyImpl, onSetLegacyComponentProperties) {
        // Hack to make sure we don't send the same model information twice in a new object.
        // This causes the viewer to blow up.
        if (this.hasLoaded && this.isModelDataSame) return this.currentModelData;

        // TODO GVC DEPRECATION: REMOVE
        if (isLegacyImpl) {
            const models = this.initilizeGVCComponent(modelData, onSetLegacyComponentProperties);
            this.currentModelData = models;
            this.hasLoaded = true;
            return models;
        }
        // END OF TODO *********************************

        const models = [];
        const modelObject = this.createModelObject(modelData);
        if (modelObject) models.push(modelObject);

        this.currentModelData = models;
        this.hasLoaded = true;
        return models;
    }

    createModelObject = (modelData) => {
        const {
            id,
            url,
            title: displayName,
            modelType,
            accessProtocol,
            vopClientId,
            vopRedirectUrl,
            clientSecret,
            webAPIModelType,
        } = modelData;
        const modelObject = new GVCModelObject(id, displayName, url || '', this.getEnumValue(modelType, GVC_ENUM.ModelType), this.getEnumValue(accessProtocol, GVC_ENUM.AccessProtocol));

        // Filter out these items as they are used in the constructor to set specific enum values.
        // eslint-disable-next-line array-element-newline
        const filterParameters = [ 'id', 'accessProtocol', 'url', 'modelType', 'displayName' ];
        // eslint-disable-next-line array-element-newline
        Object.entries(modelData).forEach(([ key, value ]) => {
                const updateObject = filterParameters.find((fil) => fil === key);
                if (!updateObject) {
                    modelObject[key] = value;
                }
            });

        if (this.getEnumValue(accessProtocol, GVC_ENUM.AccessProtocol) !== GVC_ENUM.AccessProtocol.FILE) {
            if (this.getEnumValue(modelType, GVC_ENUM.ModelType) === GVC_ENUM.ModelType.EDGES) {
                const newIdMap = new GDS2DIdMap();
                // eslint-disable-next-line array-element-newline
                newIdMap.InitializeData([ 'P&IDAttributes', 'DrawingID' ]);
                modelObject.idMap = newIdMap;
                modelObject.webAPIModelType = webAPIModelType && this.getEnumValue(webAPIModelType, GVC_ENUM.WEBAPI_MODEL_TYPE) || 'file';
                if (clientSecret) {
                    const samAuth = new GVCSAMAuth(vopClientId, clientSecret, vopRedirectUrl, true);
                    modelObject.authClient = samAuth;
                }
                else {
                    const oktaAuth = new GVCOKTAAuth(vopClientId, vopRedirectUrl, true);
                    modelObject.authClient = oktaAuth;
                }
            }
            else {
                modelObject.idMap = new GDS3DIdMap();
                if (clientSecret) {
                    const samAuth = new GVCSAMAuth(vopClientId, clientSecret, vopRedirectUrl, true);
                    modelObject.authClient = samAuth;
                }
                else {
                    const oktaAuth = new GVCOKTAAuth(vopClientId, vopRedirectUrl, true);
                    modelObject.authClient = oktaAuth;
                }
            }
        }

        return modelObject;
    }

    loadFileMonikerConverter(models) {
        const { accessProtocol, url } = models;
        if (this.getEnumValue(accessProtocol, GVC_ENUM.AccessProtocol) === GVC_ENUM.AccessProtocol.FILE) {
            this.monikerLookup = this.monikerLookup || new MyVrMonikerLookup();
            if (this.monikerLookup.isLoaded) {
                this.monikerLookup.closeBinFile();
            }
            this.monikerLookup.openBinFile(url);
        }
    }

    handleSelectionChanged(selectionChanged) {
        const { onSelectionChanged } = this.props;
        if (!onSelectionChanged) return;

        const { models } = this.props;
        if (!models) return;

        const {accessProtocol, modelType} = models;

        const {
            graphicId,
            isSelected,
            extraData } = selectionChanged;

        onTransitionRegistry.subscribe('ppmViewControl', this.handleOnTransition);
        if (this.getEnumValue(accessProtocol, GVC_ENUM.AccessProtocol) === GVC_ENUM.AccessProtocol.FILE) {
            if (!this.monikerLookup || graphicId === -1 || !isSelected) {
                Log.info(`No graphic id found: ${JSON.stringify(selectionChanged)}`);
                onTransitionRegistry.unsubscribe();
                onSelectionChanged(undefined);
            }
            else {
                const moniker = this.monikerLookup.findMoniker(graphicId.toString());
                onSelectionChanged(moniker.trim());
            }
        }
        else if (this.getEnumValue(modelType, GVC_ENUM.ModelType) === this.getEnumValue(accessProtocol, GVC_ENUM.AccessProtocol) === GVC_ENUM.AccessProtocol.WEB_API) {
            const oid = extraData ? extraData.oidPath.split('|')[0] : '';
            onSelectionChanged(oid);
        }
        else {
            onSelectionChanged(graphicId);
        }
    }

    handleOnTransition = (redirection) => {
        const { onLoadGraphicProperties } = this.props;
        const { displayHint } = redirection;
        const { displayHints } = constants;
        if (displayHint === displayHints.HINT_NO_VIEW) {
            onLoadGraphicProperties(redirection);
            return null;
        }
        if (displayHint === displayHints.HINT_IN_COMPONENT) {
            onLoadGraphicProperties(redirection);
            onTransitionRegistry.unsubscribe();
            return null;
        }

        return redirection;
    }

    handleOnViewerLoaded() {
        const {
            onViewerLoaded,
            models } = this.props;

        this.loadFileMonikerConverter(models);
        onViewerLoaded();
    }

    /**
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     *                                          TODO GVC DEPRECATION: REMOVE Legacy impl
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     */
// #region TODO GVC DEPRECATION
     initilizeGVCComponent(modelData, onSetLegacyComponentProperties) {
        const models = [];
        let properties = {};
        if (Array.isArray(modelData)) {
            modelData.forEach((model) => {
                const modelObject = this.deprecateCreateModelObject(model);
                properties = modelObject ? this.configureSettings(modelObject.struct, model, properties) : undefined;
                if (modelObject) models.push(modelObject);
            });
        }
        else {
            const modelObject = this.deprecateCreateModelObject(modelData);
            properties = modelObject ? this.configureSettings(modelObject.struct, modelData, properties) : undefined;
            if (modelObject) models.push(modelObject);
        }

        onSetLegacyComponentProperties(properties);
        return models;
    }

    configureSettings = (modelStruct, modelRecords, settings) => {
        const strippedProps = Object.assign({}, modelRecords);
        Object.getOwnPropertyNames(modelStruct).map((name) => delete strippedProps[name]);

        return { ...settings, ...strippedProps };
    }

    createTransformObject = (transformJson) => {
        const transformObject = JSON.parse(transformJson);
        if (!transformObject || !transformObject.matrix) return null;

        const userTransform = new GVCTransform(transformObject.matrix.m00, transformObject.matrix.m01, transformObject.matrix.m02, transformObject.matrix.m03,
            transformObject.matrix.m10, transformObject.matrix.m11, transformObject.matrix.m12, transformObject.matrix.m13, transformObject.matrix.m20, transformObject.matrix.m21, transformObject.matrix.m22, transformObject.matrix.m23,
            transformObject.matrix.m30, transformObject.matrix.m31, transformObject.matrix.m32, transformObject.matrix.m33);
        if (transformObject.rotation) {
            userTransform.rotation = new Vec3(transformObject.rotation.x, transformObject.rotation.y, transformObject.rotation.z);
        }
        if (transformObject.scale) {
            userTransform.scale = new Vec3(transformObject.scale.x, transformObject.scale.y, transformObject.scale.z);
        }
        if (transformObject.translation) {
            userTransform.translation = new Vec3(transformObject.translation.x, transformObject.translation.y, transformObject.translation.z);
        }
        if (transformObject['use-matrix']) {
            userTransform.useMatrix = transformObject['use-matrix'];
        }

        return userTransform;
    }

    deprecateCreateModelObject = (modelRecords) => {
        const {
            id,
            url,
            displayName,
            modelType,
            accessProtocol,
            cameraHeading,
            cameraProjection,
            cameraLocks,
            userTransform,
            // backgroundColor,
        } = modelRecords;
        if (!id || !url) return null;
        const modelObject = new GVCModelObject(id, displayName, url, modelType, accessProtocol);
        modelObject.struct = Object.getPrototypeOf(modelObject);
        modelObject.key = id;

        // eslint-disable-next-line no-bitwise
        if (cameraHeading) modelObject.cameraHeading = cameraHeading;
        if (cameraProjection) modelObject.cameraProjection = cameraProjection;
        if (cameraLocks) modelObject.cameraLocks = cameraLocks;
        if (userTransform) {
            modelObject.userTransform = this.createTransformObject(userTransform);
        }
        if (modelType === GVC_ENUM.ModelType.EDGES) {
            modelObject.backgroundColor = new MCDColor(255, 255, 255, 1.0);
        }

        return modelObject;
    }

    /**
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     *                                                          END OF TODO:
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     * *********************************************************************************************************************************************
     */
// #endregion TODO GVC DEPRECATION
}

export default PPMViewControl;
